import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { DemoConfiguration } from "data/models";
import { fetchDemos } from "data/queries-common";
import addressAutocompleteDark from "icons/address-autocomplete-dark.png";
import addressAutocompleteLight from "icons/address-autocomplete-light.png";
import chatswood from "icons/chatswood.png";
import easementsDark from "icons/easements-dark.png";
import easementsLight from "icons/easements-light.png";
import emissionsMetric from "icons/emissionsMetric.jpg";
import floodDark from "icons/flood-dark.png";
import floodLight from "icons/flood-light.png";
import localityAutocompleteDark from "icons/locality-autocomplete-dark.png";
import localityAutocompleteLight from "icons/locality-autocomplete-light.png";
import nabDark from "icons/nab-dark.png";
import nabLight from "icons/nab-light.png";
import vegChangeDark from "icons/vegetation-change.jpg";
import vegChangeLight from "icons/vegetation-change.jpg";
import lulcCarathool from "icons/lulc-carrathool.jpeg";
import propertyInsightsDark from "icons/property-insights-dark.png";
import propertyInsightsLight from "icons/property-insights-light.png";
import electricityMarketIcon from "icons/Stylized-Lightning-Bolt.png";
import { Search, Wand } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { useTheme } from "@/components/theme-provider";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AgriculturalLulcPage } from "../demos/agricultural-lulc/agricultural-lulc-page";
import { VegetationChangePage } from "../demos/vegetation-change/vegetation-change-page";
import { ChatswoodBuildings } from "../demos/chatswood-buildings/chatswood-buildings";
import { Easements } from "../demos/easements/easements";
import { ElectricityMarket } from "../demos/electricity-market/electricity-market";
import { EmissionMetricPage } from "../demos/emission-metric/emision-metric-page";
import { FloodDataPage } from "../demos/flood-data/flood-data-page";
import { NabLandUsePage } from "../demos/nab-land-use/nab-land-use-page";
import { NationalBuildingsChatswood } from "../demos/national-buildings-chatswood/national-buildings-chatswood";
import { predictiveAddress } from "../demos/predictive-address/predictive-address-demo";
import { predictiveSuburb } from "../demos/predictive-suburb/predictive-suburb-demo";
import { PropertyInsights } from "../demos/property-insights/property-insights";
import { DemoCard } from "./demo-card";
import { PageTitle } from "@/components/page-title";

// The NAB card and the generic Geoscape card share one page component; only
// the portfolio layer's label differs.
const NabVegetationChangePage = () => (
  <VegetationChangePage layerLabels={{ portfolio: "NAB Portfolio" }} />
);

export interface DemoItem {
  id: string;
  title: string;
  categories: string[];
  description: string;
  path: string;
  darkThumbnail: string;
  lightThumbnail: string;
  component: React.ComponentType<any>;
  overlay?: boolean;
}

export const DataDemos: DemoItem[] = [
  {
    id: "emissions-metric",
    title: "Emissions Metric",
    categories: ["Climate"],
    description:
      "Visualise emissions metrics for property portfolios, supporting environmental compliance and informed decision making.",
    path: Routes.emissionsMetric,
    darkThumbnail: emissionsMetric,
    lightThumbnail: emissionsMetric,
    component: EmissionMetricPage,
    overlay: true,
  },
  {
    id: "predictive-address",
    title: "Address Autocomplete",
    categories: ["Risk", "UX", "Service Delivery", "Ecommerce"],
    description:
      "Predictive address auto-completion with real-time map visualisation. Enhance user experience and operational efficiency.",
    path: Routes.predictiveAddress,
    darkThumbnail: addressAutocompleteDark,
    lightThumbnail: addressAutocompleteLight,
    component: predictiveAddress,
  },
  {
    id: "predictive-locality",
    title: "Locality Autocomplete",
    categories: ["Risk", "UX", "Service Delivery", "Ecommerce"],
    description:
      "Predictive locality auto-completion with real-time map visualisation. Enhance user experience and operational efficiency.",
    path: Routes.predictiveLocality,
    darkThumbnail: localityAutocompleteDark,
    lightThumbnail: localityAutocompleteLight,
    component: predictiveSuburb,
  },
  {
    id: "easements",
    title: "Easements",
    categories: ["Utilities", "Access", "Rights", "Development"],
    description:
      "Explore our easement data demo — gain property insights to streamline planning and identify restrictions",
    path: Routes.easements,
    darkThumbnail: easementsDark,
    lightThumbnail: easementsLight,
    component: Easements,
    overlay: true,
  },
  {
    id: "flood-data-demo",
    title: "Flood Data Demo",
    categories: [],
    description:
      "See flood risk through new eyes. Our interactive demonstration reveals how the Ingham community was affected by flooding, using sophisticated modelling and class-leading spatial insights from Geoscape that go beyond traditional risk assessments.",
    path: Routes.floodDataDemo,
    darkThumbnail: floodDark,
    lightThumbnail: floodLight,
    component: FloodDataPage,
    overlay: true,
  },
  {
    id: "nab-land-use",
    title: "NAB land use",
    categories: [],
    description: "",
    path: Routes.nabLandUse,
    darkThumbnail: nabDark,
    lightThumbnail: nabLight,
    component: NabLandUsePage,
    overlay: true,
  },
  {
    id: "nab-deforestation",
    title: "NAB Vegetation Change App",
    categories: [
      "Forestry",
      "Environmental Monitoring",
      "Risk Management",
      "Compliance",
    ],
    description:
      "The NAB Vegetation Change App helps case managers assess vegetation change at the property level across the South Burnett LGA, QLD. Review vegetation changes, regrowth, and environmental context at both the individual parcel and whole-portfolio level.",
    path: Routes.nabDeforestation,
    darkThumbnail: vegChangeDark,
    lightThumbnail: vegChangeLight,
    component: NabVegetationChangePage,
    overlay: true,
  },
  {
    id: "geoscape-vegetation-change",
    title: "Geoscape Vegetation Change",
    categories: [
      "Forestry",
      "Environmental Monitoring",
      "Risk Management",
      "Compliance",
    ],
    description:
      "Assess vegetation change at the property level. Review vegetation loss, regrowth, and environmental context at both the individual parcel and whole-portfolio level, using the South Burnett LGA, QLD as a worked example.",
    path: Routes.vegetationChange,
    darkThumbnail: vegChangeDark,
    lightThumbnail: vegChangeLight,
    component: VegetationChangePage,
    overlay: true,
  },
  {
    id: "agricultural-lulc",
    title: "National LULC (Carrathool)",
    categories: [
      "Land Use & Land Cover",
      "Forestry",
      "Environmental Monitoring",
      "Risk Management",
    ],
    description:
      "New Land Use Land Cover (LULC) demo - See how Australia’s land is used — parcel by parcel, consistently and over time. Using Carrathool Shire in western NSW, one of the state’s richest agricultural areas, the LULC demo shows what’s happening at an individual parcel level.",
    path: Routes.agriculturalLulc,
    darkThumbnail: lulcCarathool,
    lightThumbnail: lulcCarathool,
    component: AgriculturalLulcPage,
    overlay: true,
  },
  {
    id: "property-insights",
    title: "Property Insights",
    categories: [],
    description: "",
    path: Routes.propertyInsights,
    darkThumbnail: propertyInsightsDark,
    lightThumbnail: propertyInsightsLight,
    component: PropertyInsights,
    overlay: true,
  },
  {
    id: "chatswood-buildings",
    title: "Chatswood Demo",
    categories: [
      "Built Environment",
      "Risk",
      "Planning",
      "Surface Cover",
      "Digital Twin",
    ],
    description:
      "This demo showcases the Chatswood area as an interactive 3D digital twin. It brings together a wide range of national datasets, visualised in one place, allowing users to explore and experience Chatswood in a rich, data-driven environment.",
    path: Routes.chatswoodBuildings,
    darkThumbnail: chatswood,
    lightThumbnail: chatswood,
    component: ChatswoodBuildings,
  },
  {
    id: "national-buildings-chatswood",
    title: "National Buildings (Chatswood)",
    categories: [
      "Built Environment",
      "Buildings",
      "National Data",
      "Spatial Analysis",
    ],
    description:
      "New National Buildings Product. Explore the interactive demo of Chatswood to see the data in action and experience the product's capabilities. Once you've explored, you can also download a sample dataset to take a closer look at the new Insight Pack.",
    path: Routes.nationalBuildingsChatswood,
    darkThumbnail: chatswood,
    lightThumbnail: chatswood,
    component: NationalBuildingsChatswood,
    overlay: true,
  },
  {
    id: "electricity-market",
    title: "Electricity Market",
    categories: ["Energy", "Infrastructure", "Market Data"],
    description:
      "Proof of concept demo for exploring the electricity market data with visualisations. Missing WA and NT electricity data. Emissions data has been estimated, but it is possible to obtain this data too.",
    path: Routes.electricityMarket,
    darkThumbnail: electricityMarketIcon,
    lightThumbnail: electricityMarketIcon,
    component: ElectricityMarket,
    overlay: true,
  },
];

export const DemosPage = () => {
  const [identityState] = useIdentity();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [searchQuery, setSearchQuery] = useState("");

  const identityId = resolveIdentityId(identityState);

  const demosQuery = useQuery({
    queryKey: [QueryKeys.demosKey, identityId],
    queryFn: () => fetchDemos(),
  });

  const getDemoItem = (demoId: string): DemoItem | undefined => {
    return DataDemos.find((item) => item.id === demoId);
  };

  const filterDemo = (demo: DemoItem) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      demo.title.toLowerCase().includes(query) ||
      demo.description.toLowerCase().includes(query) ||
      demo.categories.some((c) => c.toLowerCase().includes(query))
    );
  };

  const availableDemos: {
    item: DemoItem;
    status: DemoConfiguration["status"];
  }[] =
    demosQuery.data?.flatMap((d: DemoConfiguration) => {
      const item = getDemoItem(d.id);
      if (!item || !filterDemo(item)) return [];
      return [{ item, status: d.status }];
    }) ?? [];

  return (
    <>
      <PageTitle
        icon={Wand}
        title={"Demos & Prototypes"}
        description={
          "Explore hands-on demonstrations of Geoscape data products. Each demo showcases real datasets and insights across different use cases."
        }
      />
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search demos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      <ReactQueryErrorWrapper queries={[demosQuery]} mutations={[]} />

      <div className="grid gap-4 xl:grid-cols-2 3xl:grid-cols-3">
        {demosQuery.isPending &&
          DataDemos.map((_, index) => (
            <Skeleton key={index} className="h-62.5 w-full" />
          ))}

        {demosQuery.isSuccess &&
          availableDemos.map(({ item, status }) => (
            <DemoCard
              key={item.id}
              {...item}
              status={status}
              thumbnail={isDark ? item.darkThumbnail : item.lightThumbnail}
            />
          ))}
      </div>

      {demosQuery.isSuccess && availableDemos.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Search className="size-8 text-muted-foreground mb-2" />
          <p className="text-sm font-medium mb-1">No demos found</p>
          <p className="text-sm text-muted-foreground">
            Try adjusting your search to find what you&apos;re looking for.
          </p>
        </div>
      )}
    </>
  );
};
